// app/utils/registerSchemas.ts
import {z} from 'zod'

// Regras da senha do usuário
export const passwordRules = [
  { label: 'Mínimo de 12 caracteres', test: (p: string) => p.length >= 12 },
  { label: 'Letra maiúscula e minúscula', test: (p: string) => /[a-z]/.test(p) && /[A-Z]/.test(p) },
  { label: 'Pelo menos um número', test: (p: string) => /\d/.test(p) },
  { label: 'Pelo menos um símbolo', test: (p: string) => /[\W_]/.test(p) }
];

function isValidCPF(cpf: string) {
    cpf = cpf.replace(/\D/g, '')
    if (cpf.length !== 11) return false
    if (/^(\d)\1+$/.test(cpf)) return false

    let sum = 0
    for (let i = 0; i < 9; i++) sum += parseInt(cpf.charAt(i)) * (10 - i)
    let rev = 11 - (sum % 11)
    if (rev === 10 || rev === 11) rev = 0
    if (rev !== parseInt(cpf.charAt(9))) return false

    sum = 0
    for (let i = 0; i < 10; i++) sum += parseInt(cpf.charAt(i)) * (11 - i)
    rev = 11 - (sum % 11)
    if (rev === 10 || rev === 11) rev = 0
    if (rev !== parseInt(cpf.charAt(10))) return false

    return true
};

const validateBrazilianDocs = (data: any, ctx: z.RefinementCtx) => {
  if (data.nationality === 'Brasileiro') {
    if (!data.cpf || !isValidCPF(data.cpf)) {
      ctx.addIssue({
        code: 'custom',
        message: 'CPF inválido',
        path: ['cpf']
      })
    }
    if (!data.residents) {
      ctx.addIssue({
        code: 'custom',
        message: 'Informe o número de residentes',
        path: ['residents']
      })
    } else if (Number(data.residents) <= 0) {
      ctx.addIssue({
        code: 'custom',
        message: 'Deve ser maior que zero',
        path: ['residents']
      })
    }
  }
};

export const step0BaseSchema = z.object({
    username: z.string().min(4, { message: 'Mínimo 4 caracteres' }),
    email: z.email({ message: 'E-mail inválido' }),
    confirmEmail: z.string().min(1, { message: 'Confirme seu e-mail' }).email({ message: 'E-mail inválido' }),
    password: z.string().superRefine((val, ctx) => {
        // Encontra todas as regras que NÃO foram atendidas
        const failedRules = passwordRules.filter(rule => !rule.test(val))
        if (failedRules.length > 0) {
            // Operador lógico sugerido para consolidar os erros:
            // Se faltar mais de um critério, mostra a mensagem genérica.
            // Se faltar APENAS UM critério (ex: esqueceu o símbolo), avisa exatamente qual é.
            const errorMessage = failedRules.length > 1
                ? 'Senha fora do padrão exigido'
                : failedRules[0]?.label
            ctx.addIssue({
                code: 'custom',
                message: errorMessage
            })
        }
    }),
    confirmPassword: z.string().min(1, { message: 'Confirme sua senha' }),
    acceptedTerms: z.literal(true, { error: 'Você deve aceitar os termos da LGPD' })
});

export const step0Schema = step0BaseSchema.refine(data => data.email === data.confirmEmail, {
    message: 'E-mails não coincidem',
    path: ['confirmEmail'],
}).refine(data => data.password === data.confirmPassword, {
    message: 'Senhas não coincidem',
    path: ['confirmPassword'],
});

export const step1Schema = z.object({
    name: z.string().min(1, 'Informe seu nome'),
    surname: z.string().min(1, 'Informe seu sobrenome'),
    birthDate: z.string()
        .min(1, 'Informe sua data de nascimento')
        .regex(/^\d{2}\/\d{2}\/\d{4}$/, 'Formato inválido (DD/MM/AAAA)')
        .refine((val) => {
            const [day, month, year] = val.split('/').map(Number)
            if (!day || !month || !year) return false
            const date = new Date(year, month - 1, day)
            return (
                date.getFullYear() === year &&
                date.getMonth() === month - 1 &&
                date.getDate() === day &&
                year > 1900 &&
                year <= new Date().getFullYear()
            )
        }, 'Data inválida'),
    isStudent: z.string().min(1, 'Selecione uma opção'),
    howMeet: z.string().min(1, 'Selecione uma opção')
});

export const step2Schema = z.object({
    country: z.string().min(1, 'Informe o país'),
    state: z.string().min(1, 'Informe seu estado'),
    city: z.string().min(1, 'Informe sua cidade')
});

export const step3BaseSchema = z.object({
    nationality: z.string(),
    cpf: z.string().optional(),
    residents: z.union([z.string(), z.number()]).optional(),
    income: z.string().min(1, 'Informe a renda familiar')
});

export const step3Schema = step3BaseSchema.superRefine(validateBrazilianDocs);

export const fullFormSchema = z.object({
  ...step0BaseSchema.shape,
  ...step1Schema.shape,
  ...step2Schema.shape,
  ...step3BaseSchema.shape
})
.refine(data => data.email === data.confirmEmail, { message: 'E-mails não coincidem', path: ['confirmEmail'] })
.refine(data => data.password === data.confirmPassword, { message: 'Senhas não coincidem', path: ['confirmPassword'] })
.superRefine(validateBrazilianDocs);
